import { DefaultHeaders, RequestMetaData, RestProxyResponse } from "./client";
import { ZendeskSidebarTicket } from "./wiseOwlZendesk";

/**
 * SHARED
 */
export interface WiseOwlApiResponse<Type> {
  data: Type;
  message: string;
  statusCode: number;
  success: boolean;
}

export enum WiseOwlIngress {
  osc = "osc",
  oscUngated = "oscUngated",
  zendesk = "zendesk",
  salesforce = "salesforce",
}

export type WiseOwlGetAccessTokenHeaders =
  | WiseOwlGetAccessTokenHeadersSalesforce
  | WiseOwlGetAccessTokenHeadersZendesk;

export interface WiseOwlGetAccessTokenHeadersSalesforce extends DefaultHeaders {
  ["TODO-SF-HEADER"]: string;
}

export interface WiseOwlGetAccessTokenHeadersZendesk extends DefaultHeaders {
  ["zendesk-user-id"]: string;
}

export type WiseOwlHeaders = WiseOwlHeadersOsc | WiseOwlHeadersZendesk;

export interface WiseOwlHeadersOsc extends DefaultHeaders {
  ["x-twilio-csrf"]: string;
}

export interface WiseOwlHeadersZendesk
  extends WiseOwlGetAccessTokenHeadersZendesk {
  ["wozd-auth"]: string;
}

export interface WiseOwlHeadersSalesforce
  extends WiseOwlGetAccessTokenHeadersSalesforce {
  ["TODO-SF-SECOND-HEADER"]: string;
}

export interface WiseOwlCreateUpdateBodyOsc {
  input: string;
  stream: boolean;
  recaptchaToken: string;
  stargateUngatedUserId?: never;
}

export interface WiseOwlCreateUpdateBodyOscUngated {
  input: string;
  stream: boolean;
  recaptchaToken: string;
  stargateUngatedUserId: string;
}

// Used for internal facing ingress (Zendesk | Salesforce)
export interface WiseOwlCreateUpdateBodyInternal {
  input: string;
  stream?: boolean;
  recaptchaToken?: never;
  stargateUngatedUserId?: never;
  ticket?: ZendeskSidebarTicket;
}

export interface WiseOwlCreateFeedbackBody {
  feedback: string;
  positive: boolean;
  recaptchaToken: string;
  stargateUngatedUserId?: never;
}

export interface WiseOwlCreateFeedbackBodyUngated {
  feedback: string;
  positive: boolean;
  recaptchaToken: string;
  stargateUngatedUserId: string;
}

export interface WiseOwlCreateFeedbackBodyInternal {
  feedback: string;
  positive: boolean;
  recaptchaToken?: never;
  stargateUngatedUserId?: never;
}

export enum MessageCompletionStatus {
  InProgress = "inProgress",
  Complete = "complete",
  Error = "error",
}

export interface WiseOwlSyncDocumentContent {
  blocks: WiseOwlBlock[];
  status: MessageCompletionStatus;
}

/**
 * GET ACCESS TOKEN
 */
export type WiseOwlGetAccessTokeByIngressArgs =
  | WiseOwlGetAccessTokeByIngressArgsSalesforce
  | WiseOwlGetAccessTokeByIngressArgsZendesk;

export interface WiseOwlGetAccessTokeByIngressArgsSalesforce
  extends RequestMetaData {
  headers: WiseOwlGetAccessTokenHeadersSalesforce;
  ingress: WiseOwlIngress.salesforce;
}

export interface WiseOwlGetAccessTokeByIngressArgsZendesk
  extends RequestMetaData {
  headers: WiseOwlGetAccessTokenHeadersZendesk;
  ingress: WiseOwlIngress;
}

export interface WiseOwlGetAccessTokenResponseData {
  token: string;
}

/**
 * GET CHAT BY ID
 */
export interface WiseOwlGetChatByIdArgs extends RequestMetaData {
  chatId: string;
}

export interface WiseOwlGetChatsByUserResponseData {
  chats: WiseOwlChat[];
}

export interface WiseOwlGetChatByIdResponseData {
  chat: WiseOwlChat;
  blocks: WiseOwlBlock[];
}

/**
 * GET CHAT ABOUT TICKET ID
 */
export interface WiseOwlGetChatAboutTicketIdArgs extends RequestMetaData {
  aboutTicketId: number;
  headers: WiseOwlGetAccessTokenHeadersZendesk;
  ingress: WiseOwlIngress;
}

export interface WiseOwlGetChatAboutTicketIdResponseData {
  chats: WiseOwlChat[];
  blocks: WiseOwlBlock[];
}

/**
 * GET CHAT SUMMARY BY ID
 */
export interface WiseOwlGetChatSummaryByIdArgs extends RequestMetaData {
  chatId: string;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlGetUngatedChatSummaryByIdArgs extends RequestMetaData {
  chatId: string;
  body: WiseOwlGetUngatedChatSummaryBody;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlGetChatSummaryByIdResponseData {
  subject: string;
  description: string;
  wasTranslated: boolean;
}

/**
 * CREATE CHAT
 */
export type WiseOwlCreateChatArgs =
  | WiseOwlCreateArgsOsc
  | WiseOwlCreateUngatedArgsOsc
  | WiseOwlCreateArgsZendesk
  | WiseOwlCreateArgsSalesforce;

export type WiseOwlCreateFeedbackArgs =
  | WiseOwlCreateFeedbackArgsOsc
  | WiseOwlCreateUngatedFeedbackArgsOsc
  | WiseOwlCreateFeedbackArgsZendesk;

export interface WiseOwlCreateArgsOsc extends RequestMetaData {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateUpdateBodyOsc;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlCreateUngatedArgsOsc extends RequestMetaData {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateUpdateBodyOscUngated;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlCreateArgsSalesforce extends RequestMetaData {
  ingress: WiseOwlIngress.salesforce;
  body: WiseOwlCreateUpdateBodyInternal;
  headers: WiseOwlHeadersSalesforce;
}

export interface WiseOwlCreateArgsZendesk extends RequestMetaData {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateUpdateBodyInternal;
  headers: WiseOwlHeadersZendesk;
}

/**
 * CREATE FEEDBACK
 */
export interface WiseOwlCreateChatResponseData {
  accessToken: string;
  chatId: string;
  syncDocumentSid: string;
}

export interface WiseOwlCreateFeedbackDefaultArgs extends RequestMetaData {
  chatId: string;
  messageId: string;
}

export interface WiseOwlCreateFeedbackArgsOsc
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateFeedbackBody;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlCreateUngatedFeedbackArgsOsc
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateFeedbackBodyUngated;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlCreateFeedbackArgsZendesk
  extends WiseOwlCreateFeedbackDefaultArgs {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateFeedbackBodyInternal;
  headers: WiseOwlHeadersZendesk;
}

/**
 * UPDATE CHAT
 */
export type WiseOwlUpdateChatArgs =
  | WiseOwlUpdateChatArgsOsc
  | WiseOwlUpdateUngatedArgsOsc
  | WiseOwlUpdateArgsSalesforce
  | WiseOwlUpdateArgsZendesk;

export interface WiseOwlUpdateChatArgsBase extends RequestMetaData {
  chatId: string;
}

export interface WiseOwlUpdateChatArgsOsc extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.osc;
  body: WiseOwlCreateUpdateBodyOsc;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlUpdateUngatedArgsOsc extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.oscUngated;
  body: WiseOwlCreateUpdateBodyOscUngated;
  headers: WiseOwlHeadersOsc;
}

export interface WiseOwlUpdateArgsSalesforce extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.salesforce;
  body: WiseOwlCreateUpdateBodyInternal;
  headers: WiseOwlHeadersSalesforce;
}

export interface WiseOwlUpdateArgsZendesk extends WiseOwlUpdateChatArgsBase {
  ingress: WiseOwlIngress.zendesk;
  body: WiseOwlCreateUpdateBodyInternal;
  headers: WiseOwlHeadersZendesk;
}

export interface WiseOwlUpdateChatResponseData {
  accessToken: string;
  syncDocumentSid: string;
}

/**
 * @TODO !IMPORTANT
 * Last update: 03.04.2024
 *
 * We eventually want to be able to import these types from
 * ops-services-platform. In the interim, we have duplicated them here. It is
 * crucial to ensure that this file is in sync with
 * https://github.com/twilio-internal/ops-services-platform/blob/main/libs/wise-owl/shared/blocks/src/lib/definitions/types.ts
 */

/**
 * BLOCKS
 */
export type WiseOwlBlock =
  | MessageBlock
  | ToolCallBlock
  | LinkGroupBlock
  | ButtonGroupBlock
  | PromptSuggestionGroupBlock
  | SubmitTicketFormBlock
  | ToolCallStatusLineBlock;

export enum WiseOwlBlockType {
  Message = "message",
  ToolCall = "tool-call",
  LinkGroup = "link-group",
  ButtonGroup = "button-group",
  PromptSuggestionGroup = "prompt-suggestion-group",
  SubmitTicketForm = "submit-ticket-form",
  ToolCallStatusLine = "tool-call-status-line",
}

export enum WiseOwlBlockDisplayType {
  Persistent = "persistent",
  Transient = "transient",
}

export interface WiseOwlBaseBlock<
  Type extends WiseOwlBlockType,
  Values extends object
> {
  type: Type;
  // Persistent blocks are always displayed. Transient blocks are only displayed
  // if they are the most recent message. This is applicable to something like a
  // button where we don't want to render it when it is no longer relevant. We
  // can add more display types as needed to support future use cases.
  display: WiseOwlBlockDisplayType;
  values: Values;
}

export enum MessageBlockRole {
  Assistant = "assistant",
  User = "human",
}

export interface BaseMessageValues {
  id: string;
  role: MessageBlockRole;
  message: string;
  retryId?: string;
  success?: boolean;
  createChatOnRetry?: boolean;
}

export interface CompleteMessageValues extends BaseMessageValues {
  isComplete: true;
  delta: null;
}

export interface IncompleteMessageValues extends BaseMessageValues {
  isComplete: false;
  delta: string;
}

export type MessageBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.Message,
  CompleteMessageValues | IncompleteMessageValues
>;

export type ToolCallBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ToolCall,
  {
    name: string;
    input: string;
    output: string;
    formattedOutput: string;
  }
>;

export interface LinkValues {
  preview: string;
  title: string;
  url: string;
}

export type LinkGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.LinkGroup,
  { links: LinkValues[] }
>;

export enum WiseOwlEventName {
  SubmitTicket = "submit-ticket",
}

export type EventAction = WiseOwlBaseAction<
  WiseOwlActionType.Event,
  {
    name: WiseOwlEventName;
    metadata: Record<string, unknown>;
  }
>;

export type RedirectAction = WiseOwlBaseAction<
  WiseOwlActionType.Redirect,
  {
    redirectUrl: string;
  }
>;

export enum WiseOwlActionType {
  Event = "event",
  Redirect = "redirect",
}
export interface WiseOwlBaseAction<
  Type extends WiseOwlActionType,
  Values extends object
> {
  type: Type;
  values: Values;
}

export type WiseOwlAction = EventAction | RedirectAction;

export interface ButtonValues {
  text: string;
  action: WiseOwlAction;
}

export type ButtonGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ButtonGroup,
  {
    buttons: ButtonValues[];
  }
>;

export type SubmitTicketFormBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.SubmitTicketForm,
  {
    ticketDescription: string;
    ticketBody: string;
    optionalQuestions: string[];
  }
>;

export interface PromptSuggestionCategory {
  name: string;
  suggestions: string[];
}

export type PromptSuggestionGroupBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.PromptSuggestionGroup,
  {
    categories: PromptSuggestionCategory[];
  }
>;

export type ToolCallStatusLineBlock = WiseOwlBaseBlock<
  WiseOwlBlockType.ToolCallStatusLine,
  {
    content: string;
  }
>;

/**
 * CHATS
 */
/**
 * * Note: These types are generated in the API by Prisma and do not exists
 * * as an explicit types file in OSP, which means the below are the best source
 * * of truth for our client-side applications. The properties are also not
 * * alphabetized to more closely match the Prisma schema. Props that are not used
 * * in OWP are also not include (e.g. slackThreadTimestamp, slackConversationId)
 */
export interface WiseOwlChat {
  id: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  title: string;
  ingress: WiseOwlApiIngress;
  slackConversationId: string | null;
  slackThreadTimestamp: string | null;
  messages: WiseOwlMessage[];
  aboutTicketId: number;
}

export type WiseOwlChatHistoryItem = Omit<WiseOwlChat, "messages">;

export interface WiseOwlMessage {
  id: string;
  createdAt: string;
  chatId: string;
  updatedAt: string;
  messageRole: MessageRole;
  content: string;
  userId: string | null;
  slackMessageTimestamp: string | null;
  isComplete: boolean | null;
  toolCalls: WiseOwlToolCall[];
}

// * Note: Tool calls should only be shown on internal applications
export interface WiseOwlToolCall {
  id: number;
  createdAt: string;
  updatedAt: string;
  messageId: number;
  name: string;
  input: string;
  output: string;
  formattedOutput: string;
}

export enum WiseOwlApiIngress {
  OneAdmin = "ONE_ADMIN",
  OneSupportCenter = "ONE_SUPPORT_CENTER",
  Salesforce = "SALESFORCE",
  Zendesk = "ZENDESK",
}

export enum MessageRole {
  User = "USER",
  Assistant = "ASSISTANT",
  System = "SYSTEM",
}

/**
 * LEGACY WISE OWL API TYPES
 * As of 2024-03-22, still used for WO 1A
 */
// WiseOwl API types

// Responses
export type FetchChatByIdResponse = RestProxyResponse<WiseOwlChat>;
export type SendMessageResponse = RestProxyResponse<SendMessageData>;
export type CreateChatResponse = RestProxyResponse<CreateChatData>;
export type FetchChatHistoryResponse = RestProxyResponse<GetChatHistoryData>;

// Request Bodies & Params
export interface CreateChatRequestBody {
  input: string;
}

export interface SendMessageRequestBody {
  input: string;
}

export interface SendMessageParams {
  chatId: string;
  chatData: SendMessageRequestBody;
  bearerToken?: string;
}

export interface SendMessageData {
  chat: WiseOwlChat;
}

export interface Chat {
  createdAt: string;
  id: number;
  ingress: string;
  slackConversationId: string | null;
  slackThreadTimestamp: string | null;
  title: string;
  updatedAt: string;
  userId: string;
}

// Supporting objects
export interface CreateChatData {
  chat: WiseOwlChat;
}

export interface GetChatHistoryData {
  chats: WiseOwlChatHistoryItem[];
}

// Legacy message - only support temporarily for 1A
export interface Message {
  chatId: string;
  content: string;
  createdAt: string;
  id: string;
  messageRole: string;
  slackMessageTimestamp: string | null;
  toolCalls: Record<string, unknown>[];
  updatedAt: string;
  userId: string | null;
}

export interface LegacyWiseOwlAuthor {
  alias: string;
  displayName: string;
  isAuthor: boolean;
  isEndUser: boolean;
  name: string;
}

// To deprecate
export interface LegacyWiseOwlMessage {
  author: LegacyWiseOwlAuthor;
  body: string;
  createdAt: string;
  htmlBody: string;
  hasError?: boolean;
  handleResend?: (retryId: string) => void;
  retryId?: string;
}

export interface ChatHistoryDropdownItem {
  id: string;
  title: string;
  updatedAt: string;
  url: string;
}

export interface WiseOwlGetUngatedChatSummaryBody {
  stargateUngatedUserId: string;
}
